import React, { useRef } from "react";
import { Upload, FileCheck2, X, Beaker, Truck, Ambulance, Stethoscope, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

// Field renderer ---------------------------------------------------------
const RadioRow = ({ value, onChange, options, name, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const active = value === o;
      return (
        <label
          key={o}
          className="text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-colors"
          style={{
            background: active ? "#0B2545" : "#FFFFFF",
            color: active ? "#FFD774" : "#475569",
            borderColor: active ? "#0B2545" : "#E2E8F0",
            fontWeight: active ? 600 : 500,
          }}
        >
          <input
            type="radio"
            name={name}
            value={o}
            checked={active}
            onChange={(e) => onChange(e.target.value)}
            className="sr-only"
          />
          {o}
        </label>
      );
    })}
  </div>
);

const SubField = ({ label, hint, children, testid }) => (
  <div data-testid={testid}>
    <Label className="mb-1.5 block text-[11px] uppercase tracking-[0.18em] font-medium" style={{ color: "#0B2545" }}>
      {label}
      {hint && <span className="ml-1 normal-case font-normal" style={{ color: "#94A3B8" }}>· {hint}</span>}
    </Label>
    {children}
  </div>
);

// File upload chip -------------------------------------------------------
const FileChip = ({ file, onClear, testid }) => (
  <div className="flex items-center gap-2 rounded-lg border p-2.5 bg-white"
       style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
    <FileCheck2 className="h-4 w-4 shrink-0" style={{ color: "#0F6D5D" }} />
    <div className="flex-1 min-w-0">
      <div className="text-xs font-medium truncate" style={{ color: "#0B2545" }}>{file.name}</div>
      <div className="text-[10px]" style={{ color: "#64748B" }}>
        {(file.size / 1024).toFixed(0)} KB
      </div>
    </div>
    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0"
            onClick={onClear} style={{ color: "#9B1C1C" }}>
      <X className="h-3.5 w-3.5" />
    </Button>
  </div>
);

const FileUploader = ({ file, onPick, onClear, accept, testid }) => {
  const ref = useRef(null);
  if (file) return <FileChip file={file} onClear={onClear} testid={`${testid}-chip`} />;
  return (
    <div>
      <input
        ref={ref}
        type="file"
        className="hidden"
        accept={accept || ".pdf,.jpg,.jpeg,.png,.heic,.heif,.docx"}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
        }}
        data-testid={testid}
      />
      <Button type="button" variant="outline" className="h-10 w-full" onClick={() => ref.current?.click()}
              data-testid={`${testid}-trigger`}>
        <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Lab Order (PDF / image)
      </Button>
      <p className="text-[10px] mt-1" style={{ color: "#94A3B8" }}>
        Max 15 MB. We'll match tube types & supplies internally — no need to specify.
      </p>
    </div>
  );
};

// Service-specific sub-section -------------------------------------------
const SectionHeader = ({ icon: Icon, title, blurb }) => (
  <div className="mb-4 flex items-start gap-3">
    <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
          style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
      <Icon className="h-4 w-4" />
    </span>
    <div>
      <div className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>{title}</div>
      <p className="text-xs mt-0.5" style={{ color: "#475569" }}>{blurb}</p>
    </div>
  </div>
);

// ========================================================================
// 1. PHLEBOTOMY
// ========================================================================
export const PhlebotomyIntake = ({ data, setData, file, setFile }) => (
  <div className="rounded-xl border p-5" style={{ borderColor: "#E2E8F0", background: "rgba(11,37,69,0.02)" }}
       data-testid="intake-phlebotomy">
    <SectionHeader
      icon={Beaker}
      title="Phlebotomy Service Details"
      blurb="A few quick details so dispatch can prep the right tubes, supplies, and timing — you don't need to know specifics."
    />
    <div className="grid gap-4">
      <SubField label="Do you have a lab order?" testid="phleb-has-order">
        <RadioRow
          name="has_lab_order"
          value={data.has_lab_order || ""}
          onChange={(v) => setData("has_lab_order", v)}
          options={["Yes", "No"]}
        />
      </SubField>

      <SubField label="Upload Lab Order" hint="Optional but speeds review" testid="phleb-upload">
        <FileUploader file={file} onPick={setFile} onClear={() => setFile(null)} testid="phleb-file" />
      </SubField>

      <div className="grid gap-4 sm:grid-cols-2">
        <SubField label="Testing Laboratory" testid="phleb-lab">
          <Select value={data.testing_lab || ""} onValueChange={(v) => setData("testing_lab", v)}>
            <SelectTrigger className="h-10" data-testid="phleb-lab-trigger"><SelectValue placeholder="Select lab" /></SelectTrigger>
            <SelectContent>
              {["Quest", "Labcorp", "BioReference", "Hospital Lab", "Other"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SubField>
        <SubField label="Fasting Required?" testid="phleb-fasting">
          <RadioRow
            name="fasting"
            value={data.fasting || ""}
            onChange={(v) => setData("fasting", v)}
            options={["Yes", "No", "Unsure"]}
          />
        </SubField>
      </div>

      <SubField label="Collection Location Type" testid="phleb-location">
        <Select value={data.collection_location_type || ""} onValueChange={(v) => setData("collection_location_type", v)}>
          <SelectTrigger className="h-10" data-testid="phleb-location-trigger"><SelectValue placeholder="Where is the collection?" /></SelectTrigger>
          <SelectContent>
            {["Home", "Facility", "Assisted Living", "Skilled Nursing", "Office", "Other"].map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SubField>

      <SubField label="Special Collection Instructions" hint="Optional" testid="phleb-special">
        <Textarea
          value={data.special_instructions || ""}
          onChange={(e) => setData("special_instructions", e.target.value)}
          rows={3}
          placeholder="Access codes, mobility considerations, time windows…"
          data-testid="phleb-special-input"
        />
      </SubField>
    </div>
  </div>
);

// ========================================================================
// 2. COURIER
// ========================================================================
export const CourierIntake = ({ data, setData }) => (
  <div className="rounded-xl border p-5" style={{ borderColor: "#E2E8F0", background: "rgba(11,37,69,0.02)" }}
       data-testid="intake-courier">
    <SectionHeader
      icon={Truck}
      title="Medical Courier Details"
      blurb="What we're moving, where it's going, and how fast."
    />
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SubField label="Item Type" testid="courier-item">
          <Select value={data.item_type || ""} onValueChange={(v) => setData("item_type", v)}>
            <SelectTrigger className="h-10" data-testid="courier-item-trigger"><SelectValue placeholder="What's the courier carrying?" /></SelectTrigger>
            <SelectContent>
              {["Specimens", "Medical Records", "Medical Supplies", "Pharmaceuticals", "Equipment", "Other"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SubField>
        <SubField label="Priority" testid="courier-priority">
          <Select value={data.priority || ""} onValueChange={(v) => setData("priority", v)}>
            <SelectTrigger className="h-10" data-testid="courier-priority-trigger"><SelectValue placeholder="Select priority" /></SelectTrigger>
            <SelectContent>
              {["Routine", "Same Day", "STAT"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SubField>
      </div>
      <SubField label="Pickup Location" testid="courier-pickup">
        <Input
          value={data.pickup_location || ""}
          onChange={(e) => setData("pickup_location", e.target.value)}
          placeholder="Address or facility name"
          className="h-10"
          data-testid="courier-pickup-input"
        />
      </SubField>
      <SubField label="Delivery Location" testid="courier-delivery">
        <Input
          value={data.delivery_location || ""}
          onChange={(e) => setData("delivery_location", e.target.value)}
          placeholder="Lab or destination"
          className="h-10"
          data-testid="courier-delivery-input"
        />
      </SubField>
    </div>
  </div>
);

// ========================================================================
// 3. TRANSPORTATION
// ========================================================================
export const TransportIntake = ({ data, setData }) => (
  <div className="rounded-xl border p-5" style={{ borderColor: "#E2E8F0", background: "rgba(11,37,69,0.02)" }}
       data-testid="intake-transport">
    <SectionHeader
      icon={Ambulance}
      title="Patient Transportation Details"
      blurb="Where the patient is going and what assistance they'll need."
    />
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SubField label="Appointment Type" testid="trans-appt">
          <Select value={data.appointment_type || ""} onValueChange={(v) => setData("appointment_type", v)}>
            <SelectTrigger className="h-10" data-testid="trans-appt-trigger"><SelectValue placeholder="What kind of appointment?" /></SelectTrigger>
            <SelectContent>
              {["Doctor Appointment", "Dialysis", "Therapy", "Hospital Discharge", "Other"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SubField>
        <SubField label="Return Trip Needed?" testid="trans-return">
          <RadioRow
            name="return_trip"
            value={data.return_trip || ""}
            onChange={(v) => setData("return_trip", v)}
            options={["Yes", "No"]}
          />
        </SubField>
      </div>
      <SubField label="Destination Address" testid="trans-dest">
        <Input
          value={data.destination_address || ""}
          onChange={(e) => setData("destination_address", e.target.value)}
          placeholder="Where is the patient going?"
          className="h-10"
          data-testid="trans-dest-input"
        />
      </SubField>
      <div className="grid gap-4 sm:grid-cols-2">
        <SubField label="Mobility Assistance Needed" testid="trans-mobility">
          <Select value={data.mobility_assistance || ""} onValueChange={(v) => setData("mobility_assistance", v)}>
            <SelectTrigger className="h-10" data-testid="trans-mobility-trigger"><SelectValue placeholder="Select mobility level" /></SelectTrigger>
            <SelectContent>
              {["Ambulatory", "Walker", "Wheelchair", "Other"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SubField>
        <SubField label="Patient Name" hint="If different from requester" testid="trans-patient">
          <Input
            value={data.patient_name || ""}
            onChange={(e) => setData("patient_name", e.target.value)}
            placeholder="Patient's full name"
            className="h-10"
            data-testid="trans-patient-input"
          />
        </SubField>
      </div>
    </div>
  </div>
);

// ========================================================================
// 4. STAFFING
// ========================================================================
export const StaffingIntake = ({ data, setData }) => (
  <div className="rounded-xl border p-5" style={{ borderColor: "#E2E8F0", background: "rgba(11,37,69,0.02)" }}
       data-testid="intake-staffing">
    <SectionHeader
      icon={Stethoscope}
      title="Medical Staffing Details"
      blurb="What role you need and for how long."
    />
    <div className="grid gap-4 sm:grid-cols-2">
      <SubField label="Position Needed" testid="staff-position">
        <Select value={data.position_needed || ""} onValueChange={(v) => setData("position_needed", v)}>
          <SelectTrigger className="h-10" data-testid="staff-position-trigger"><SelectValue placeholder="Select position" /></SelectTrigger>
          <SelectContent>
            {["Phlebotomist", "Medical Assistant", "CNA", "Administrative Support", "Other"].map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SubField>
      <SubField label="Coverage Type" testid="staff-coverage">
        <Select value={data.coverage_type || ""} onValueChange={(v) => setData("coverage_type", v)}>
          <SelectTrigger className="h-10" data-testid="staff-coverage-trigger"><SelectValue placeholder="Select coverage" /></SelectTrigger>
          <SelectContent>
            {["One-Time", "Temporary", "Ongoing"].map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SubField>
    </div>
  </div>
);

// ========================================================================
// 5. CLINICAL ASSISTANCE
// ========================================================================
export const ClinicalIntake = ({ data, setData }) => (
  <div className="rounded-xl border p-5" style={{ borderColor: "#E2E8F0", background: "rgba(11,37,69,0.02)" }}
       data-testid="intake-clinical">
    <SectionHeader
      icon={ClipboardList}
      title="Clinical Assistance Details"
      blurb="What kind of operational support are you looking for?"
    />
    <SubField label="Support Needed" testid="clinical-support">
      <Select value={data.support_needed || ""} onValueChange={(v) => setData("support_needed", v)}>
        <SelectTrigger className="h-10" data-testid="clinical-support-trigger"><SelectValue placeholder="Select support type" /></SelectTrigger>
        <SelectContent>
          {["Intake", "Scheduling", "Administrative Support", "Clinical Support", "Other"].map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SubField>
  </div>
);
